import { usePage } from "@inertiajs/react"
import { AlertTriangle, ArrowRight, CalendarX, Clock, Lock } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import InsiderPostButton from "./InsiderPostButton"
import { Eyebrow, IconTile, Panel } from "./Panel"
import { formatDate } from "./format"

// The page heads and single-card states around the offer: member / welcome
// head, past due, canceled, closed and unavailable.

export function StatusPill({ variant, icon: Icon, children }) {
  return (
    <Badge variant={variant} className="gap-[5px] rounded-full px-[9px] py-[3px] text-[10.5px] font-bold tracking-[0.01em]">
      {Icon ? <Icon strokeWidth={2.2} /> : <i className="size-[6px] rounded-full bg-current" />}
      {children}
    </Badge>
  )
}

export function MemberPills({ subscription }) {
  return (
    <div className="flex flex-wrap gap-[8px]">
      <StatusPill variant="done">Active</StatusPill>
      <StatusPill variant="hold" icon={Lock}>
        Insider rate
      </StatusPill>
      {subscription?.cancelAtPeriodEnd && (
        <StatusPill variant="needsYou" icon={CalendarX}>
          Cancels on {formatDate(subscription.nextBilling)}
        </StatusPill>
      )}
    </div>
  )
}

function PageHead({ title, children, right }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-x-[16px] gap-y-[12px]">
      <div>
        <h1 className="font-display text-[25px] font-bold tracking-[-0.5px]">{title}</h1>
        {children && <p className="mt-[4px] text-[13px] leading-[1.5] text-ink-3">{children}</p>}
      </div>
      {right}
    </div>
  )
}

const B = ({ children }) => <b className="font-semibold text-muted-foreground">{children}</b>

export function MemberHead({ subscription }) {
  const { auth } = usePage().props
  const firstName = auth?.firstName
  return (
    <PageHead title={`Welcome back${firstName ? `, ${firstName}` : ""}.`} right={<MemberPills subscription={subscription} />}>
      Insider Hub ·{" "}
      {subscription?.cancelAtPeriodEnd ? (
        <>
          Your subscription ends on <B>{formatDate(subscription.nextBilling)}</B>.
        </>
      ) : (
        <>
          Active since <B>{formatDate(subscription?.activeSince)}</B>. Next billing{" "}
          <B>{formatDate(subscription?.nextBilling)}</B>.
        </>
      )}
    </PageHead>
  )
}

function StateCard({ icon: Icon, tone, eyebrow, title, children }) {
  return (
    <Panel className="flex-row items-start gap-[16px] px-[26px] py-[24px] max-[561px]:flex-col max-[561px]:px-[18px] max-[561px]:py-[20px]">
      <IconTile tone={tone}>
        <Icon strokeWidth={1.9} />
      </IconTile>
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="mt-[6px] mb-[8px] font-display text-[20px] font-bold tracking-[-0.4px]">{title}</h2>
        {children}
      </div>
    </Panel>
  )
}

const Lede = ({ children }) => <p className="max-w-[600px] text-[14px] leading-[1.6] text-muted-foreground">{children}</p>

const InfoBox = ({ children }) => (
  <div className="mt-[16px] inline-block rounded-[10px] bg-secondary px-[14px] py-[10px] text-[13px] text-muted-foreground">{children}</div>
)

const SupportLine = () => (
  <p className="mt-[12px] text-[13px] text-ink-3">
    Questions about your account?{" "}
    <a href="mailto:support@dibbly.com" className="text-foreground underline hover:text-primary">
      Contact support
    </a>
    .
  </p>
)

export function PastDue({ endpoints }) {
  return (
    <>
      <PageHead title="Insider" right={<StatusPill variant="needsYou">Past due</StatusPill>} />
      <StateCard icon={AlertTriangle} tone="amber" eyebrow="Action required" title="Payment failed">
        <Lede>
          Update your payment method to keep your Insider lock. Until payment succeeds, new orders are priced at the
          standard rate.
        </Lede>
        <InsiderPostButton
          action={endpoints?.portal}
          formClassName="mt-[18px]"
          className={cn(buttonVariants(), "h-auto px-[15px] py-[9px] text-[13px] font-bold")}
        >
          Update payment method <ArrowRight strokeWidth={2.2} />
        </InsiderPostButton>
      </StateCard>
    </>
  )
}

export function Canceled({ subscription }) {
  return (
    <>
      <PageHead title="Insider" right={<StatusPill variant="hold">Ended</StatusPill>} />
      <StateCard icon={Lock} eyebrow="Membership ended" title="Your Insider membership has ended">
        <Lede>
          An Insider membership can&apos;t be restarted once it&apos;s canceled. Your account is back to standard pricing
          on every order.
        </Lede>
        {subscription?.canceledAt && (
          <InfoBox>
            Your previous Insider subscription ended on <b className="text-foreground">{formatDate(subscription.canceledAt)}</b>.
          </InfoBox>
        )}
        <SupportLine />
      </StateCard>
    </>
  )
}

export function Closed({ closesAt }) {
  const closedOn = formatDate(closesAt)
  return (
    <>
      <PageHead title="Insider" />
      <StateCard icon={Clock} eyebrow="Enrollment closed" title="Insider enrollment is now closed">
        <Lede>Insider isn&apos;t taking new members right now. Your account stays on standard pricing for every order.</Lede>
        {closedOn && (
          <InfoBox>
            Insider enrollment closed on <b className="text-foreground">{closedOn}</b>.
          </InfoBox>
        )}
        <SupportLine />
      </StateCard>
    </>
  )
}

export function Unavailable() {
  return (
    <>
      <PageHead title="Insider" />
      <Panel className="px-[24px] py-[56px] text-center">
        <IconTile className="mx-auto mb-[18px] size-[52px] rounded-[14px] [&_svg]:size-6">
          <Clock strokeWidth={1.9} />
        </IconTile>
        <h2 className="mb-[8px] font-display text-[19px] font-bold">Insider isn&apos;t available yet</h2>
        <p className="mx-auto max-w-[440px] text-[13.5px] leading-[1.6] text-muted-foreground">
          We&apos;re finalizing setup. Check back soon, or reach out to support if you were expecting access.
        </p>
      </Panel>
    </>
  )
}
